import { create } from 'zustand'
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  getFirestore,
  Timestamp
} from 'firebase/firestore'
import Database from '../types/Database'

interface NotificationState {
  notifications: Database.NotificationDocument[]
  unreadCount: number
  isLoading: boolean
  error: string | null
  subscribe: (userId: string) => () => void
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  dismiss: (notificationId: string) => Promise<void>
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  subscribe: (userId: string) => {
    const db = getFirestore()
    set({ isLoading: true, error: null })

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('expiresAt', '>', Timestamp.now()),
      orderBy('expiresAt', 'asc'),
      orderBy('createdAt', 'desc')
    )

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const notifications = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })) as Database.NotificationDocument[]

        const unreadCount = notifications.filter(n => n.read === false).length

        set({ notifications, unreadCount, isLoading: false, error: null })
      },
      error => {
        set({ error: error.message, isLoading: false })
      }
    )

    return unsubscribe
  },

  markAsRead: async (notificationId: string) => {
    const db = getFirestore()
    await updateDoc(doc(db, 'notifications', notificationId), { read: true })
  },

  markAllAsRead: async () => {
    const db = getFirestore()
    const { notifications } = get()
    const unreadNotifications = notifications.filter(n => n.read === false)

    if (unreadNotifications.length === 0) return

    const batch = writeBatch(db)
    unreadNotifications.forEach(n => {
      batch.update(doc(db, 'notifications', n.id), { read: true })
    })
    await batch.commit()
  },

  dismiss: async (notificationId: string) => {
    const db = getFirestore()
    await deleteDoc(doc(db, 'notifications', notificationId))
  }
}))
